import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import NewScanningSessionModal from "../components/NewScanningSessionModal";
import { SCANNING_PATHS } from "Routes/paths";
import { userLogout, GetUserData } from "common/service/CommonService";
import ClinicalActivityLanding from "../components/ClinicalActivityLanding";
import { useGlobalStudy } from "../../../context/StudyContext";
import { useMasterStudyService } from "modules/Clinical/StudyDetails/api/useStudyQuery";
import { changeSidebarVisibility } from "../../../slices/thunks";
import { PERMISSION_ACTIONS, PERMISSION_MODULES } from "common/constants/PermissionConstants";
import { usePermission } from "../../../context/PermissionContext";
import AccessRestricted from "Components/Common/AccessRestricted";
import NoStudySelectedPlaceholder from "Components/Common/NoStudySelectedPlaceholder";

/**
 * ScannerPage Component
 * 
 * Main entry point for the Clinical Scanning Application.
 * Handles:
 * 1. User permission validation for clinical session access.
 * 2. Activity selection (Sample Collection, Sample Separation, Dosing).
 * 3. Opening the Session Management Modal (NewScanningSessionModal) or rendering activity landing page.
 * 4. Navigating to active scanning session execution pages with target step parameters.
 */
const ScannerPage: React.FC = () => {
  // Navigation & Redux hooks
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch: any = useDispatch();

  // Context to set global study metadata across the application
  const { setGlobalStudy } = useGlobalStudy();

  /**
   * Logs out the current user and clears session state.
   */
  const handleLogout = () => {
    userLogout(navigate);
  };

  // State to control modal visibility (auto-opens if navigated back with state.openForm = true)
  const [modalOpen, setModalOpen] = useState<boolean>(() => Boolean(location.state?.openForm));

  // State for selected clinical activity category (e.g., 'sample_collection' | 'sample_separation' | 'dosing')
  const [selectedActivity, setSelectedActivity] = useState<"sample_collection" | "sample_separation" | "dosing" | null>(
    () => location.state?.sessionCategory || null
  );

  /**
   * Auto-hide sidebar on small screens (mobile devices <= 576px) to maximize scanning viewport area.
   */
  useEffect(() => {
    if (window.innerWidth <= 576) {
      dispatch(changeSidebarVisibility("hidden"));
      document.body.classList.remove("vertical-sidebar-enable");
    }
  }, [dispatch]);

  // Permission & Study Services
  const { hasPermission } = usePermission();
  const { DropDownAllQuery: studySelectAllQuery } = useMasterStudyService({ enabledDropDown: true });

  // Check if user has permission to view all global studies across the platform
  const hasAllStudiesRight = hasPermission(PERMISSION_MODULES.GLOBAL_STUDIES, PERMISSION_ACTIONS.ALL_STUDIES);
  const user = GetUserData();

  /**
   * List of available studies populated based on user role permissions:
   * - Full access users: fetched from API drop-down query.
   * - Restricted users: filtered by user's assigned study list in local profile data.
   */
  const studyOptions: Array<{
    label: string | number;
    value: string | number;
  }> = hasAllStudiesRight
    ? (((studySelectAllQuery.data as any)?.data || []).map((study: any) => ({
        label: study.studycode,
        value: study.studyid,
      })))
    : ((user?.studies as any[])?.map((study: any) => ({
        label: study.studycode,
        value: study.studyid,
      })) || []);

  /**
   * Callback triggered when a user selects an existing session to view/resume.
   * Updates global study context and navigates to the respective session workflow step.
   * 
   * @param session - Selected session object containing study and session ID info.
   */
  const onViewSession = (session: any) => {
    // Sync selected study to global app state
    setGlobalStudy({
      studyid: session.studyid,
      studyname: session.studycode,
    });

    const rawId = session?.sessionid || session?.session_id || session?.id;
    const targetPath = SCANNING_PATHS.SESSION.replace(":id", String(rawId));
    setModalOpen(false);

    // Route to the appropriate workflow step based on session type
    if (session?.sessiontype === "dosing") {
      navigate(`${targetPath}/dosing`);
    } else if (session?.sessiontype === "sample_separation") {
      navigate(`${targetPath}/step2`);
    } else {
      navigate(`${targetPath}/step1`);
    }
  };

  // Permission Guard: User must have access to either Subject to Vacutainer OR Vacutainer to Aliquot permissions
  const isAuthorized = hasPermission(
    PERMISSION_MODULES.SESSION,
    `${PERMISSION_ACTIONS.SUBJECT_TO_VACUTAINER}, ${PERMISSION_ACTIONS.VACUTAINER_TO_ALIQUOT}`
  );

  // Render Access Restricted screen if permission check fails
  if (!isAuthorized) {
    return <AccessRestricted moduleName="Sessions" />;
  }

  // Render No Study Selected Placeholder if user has no assigned studies available
  const isQueryLoading = hasAllStudiesRight && studySelectAllQuery.isLoading;
  if (!isQueryLoading && studyOptions.length === 0) {
    return <NoStudySelectedPlaceholder moduleName="accessioning" />;
  }

  // Render New Scanning Session Modal if user opened session selector or created a session
  if (modalOpen) {
    return (
      <NewScanningSessionModal
        isOpen={modalOpen}
        toggle={() => {
          setModalOpen(false);
          setSelectedActivity(null);
          // Clean up location state after modal dismissal
          if (location.state?.openForm) {
            navigate(location.pathname, { replace: true, state: {} });
          }
        }}
        redirectPathPattern={SCANNING_PATHS.SESSION}
        isNewSessionFlow={false}
        onViewSession={onViewSession}
        defaultCategory={selectedActivity || "sample_collection"}
        handleLogout={handleLogout}
      />
    );
  }

  // Main Default View: Clinical Activity Selection Landing Page
  return (
    <ClinicalActivityLanding
      onSelectActivity={(act) => {
        setSelectedActivity(act);
        setModalOpen(true);
      }}
      handleLogout={handleLogout}
    />
  );
};

export default ScannerPage;